import type { FC } from 'react';
import { TableRow, Tooltip, Typography, useTheme } from '@mui/material';

import type {
  FollowUpActionListItem,
  FollowUpActionOrgUnit,
} from 'src/types/myFollowUp';
import { internationalDateFormat } from 'src/helper/helper';
import StatusPill from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpTable/components/StatusPill';
import TableCell from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpTable/components/TableCell';

import { styles } from './TableRow.styles';

export interface FollowUpActionTableRowProps extends FollowUpActionListItem {
  onClick: () => void;
}

const isDateBeforeToday = (inputDate: string): boolean => {
  const dateToCheck = new Date(inputDate);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return dateToCheck < today;
};

const FollowUpActionTableRow: FC<FollowUpActionTableRowProps> = ({
  id,
  orgUnits,
  kpi,
  kpiValue,
  actionPlanTitle,
  status,
  followUpOwnerName,
  lastModifiedAt,
  createdAt,
  dueAt,
  onClick,
}) => {
  const theme = useTheme();
  const dueDateWarning = isDateBeforeToday(dueAt) && status === 'Open';
  const stringifiedOrgUnits = orgUnits
    .map((orgUnitPath: FollowUpActionOrgUnit[]) => {
      const orgUnit = orgUnitPath[orgUnitPath.length - 1];
      return `${orgUnit.name} ${orgUnit.orgUnitId}`;
    })
    .join(' & ');

  return (
    <TableRow onClick={onClick} sx={styles.tableRow}>
      <TableCell>{id}</TableCell>
      <TableCell>
        <Tooltip title={stringifiedOrgUnits}>
          <span>{stringifiedOrgUnits}</span>
        </Tooltip>
      </TableCell>
      <TableCell sxOverride={{ color: theme.colors.error[600] }}>
        {kpi?.shortcut && (
          <span title={kpi?.label}>
            {kpiValue === null
              ? kpi?.shortcut
              : `${kpi?.shortcut}: ${kpiValue}%`}
          </span>
        )}
      </TableCell>
      <TableCell>
        <Tooltip title={actionPlanTitle}>
          <span>{actionPlanTitle}</span>
        </Tooltip>
      </TableCell>
      <TableCell>{followUpOwnerName}</TableCell>
      <TableCell>{internationalDateFormat(new Date(createdAt))}</TableCell>
      <TableCell>
        {lastModifiedAt
          ? internationalDateFormat(new Date(lastModifiedAt))
          : '-'}
      </TableCell>
      <TableCell>
        <Typography
          component="span"
          sx={[dueDateWarning && styles.dueDateWarning]}
        >
          {internationalDateFormat(new Date(dueAt))}
        </Typography>
      </TableCell>
      <TableCell>
        <StatusPill status={status} />
      </TableCell>
    </TableRow>
  );
};

export default FollowUpActionTableRow;
